import React, { useEffect, useState } from 'react';
import { API, authFetch } from './api';
import LogoUploader, { logoSrc, uploadLogoFile } from './LogoUploader';

const Input = ({ label, value, onChange, type = 'text', required, min, max }) => (
  <div>
    <label className="block text-xs text-[#B0BEC5] mb-1">{label}</label>
    <input type={type} value={value} required={required} min={min} max={max} onChange={(e) => onChange(e.target.value)}
      className="w-full px-2 py-1.5 rounded bg-[#0A0E27] border border-[#546E7A]/40 text-white text-sm" />
  </div>
);
const Check = ({ label, checked, onChange }) => (
  <label className="flex items-center gap-2 text-sm text-white">
    <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} /> {label}
  </label>
);

const BLANK = { name: '', display_name: '', licensed_re: true, licensed_cre: false,
  monthly_support_fee: '', aida_revenue_share_pct: '', term_start: '', launch_period_days: 30 };

// Super Admin: manage associations (create, license products, set AiDA terms, add admins).
export default function SuperAdminAssociations() {
  const [list, setList] = useState([]);
  const [error, setError] = useState('');
  const [form, setForm] = useState(BLANK);
  const [creating, setCreating] = useState(false);
  const [selected, setSelected] = useState(null);
  const [logoFile, setLogoFile] = useState(null);

  const load = () => authFetch(`${API}/api/associations`)
    .then((r) => (r.ok ? r.json() : Promise.reject(new Error('Failed to load associations'))))
    .then(setList).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);

  const create = async (e) => {
    e.preventDefault();
    setError(''); setCreating(true);
    try {
      const body = {
        ...form,
        monthly_support_fee: form.monthly_support_fee === '' ? null : Number(form.monthly_support_fee),
        aida_revenue_share_pct: form.aida_revenue_share_pct === '' ? null : Number(form.aida_revenue_share_pct),
        term_start: form.term_start || null,
        launch_period_days: Number(form.launch_period_days) || 0,
      };
      const res = await authFetch(`${API}/api/associations`, { method: 'POST', body: JSON.stringify(body) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Create failed');
      if (logoFile) {
        try { await uploadLogoFile(data.id, logoFile); }
        catch (le) { setError('Association created, but logo upload failed: ' + le.message + ' — add it under Manage.'); }
      }
      setForm(BLANK); setLogoFile(null);
      load();
    } catch (e) { setError(e.message); } finally { setCreating(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A0E27] pt-8">
      <div className="max-w-7xl mx-auto px-6">
        <h1 className="text-white text-3xl font-bold mb-6">Associations</h1>
        {error && <p className="text-red-400 mb-4">{error}</p>}
        <div className="grid lg:grid-cols-3 gap-6">
          <div className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-5">
            <h2 className="text-white font-semibold mb-1">New Association</h2>
            <p className="text-xs text-[#B0BEC5] mb-3">A REALTOR&reg; board/association that AiDA licenses. It sits above brokerages and bills them per seat.</p>
            <form onSubmit={create} className="space-y-3">
              <Input label="Legal / board name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} required />
              <Input label="Display name (shown on their branding)" value={form.display_name} onChange={(v) => setForm({ ...form, display_name: v })} required />
              <div>
                <label className="block text-xs text-[#B0BEC5] mb-1">Logo (optional)</label>
                <input type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp,image/gif"
                  onChange={(e) => setLogoFile(e.target.files && e.target.files[0])}
                  className="w-full text-xs text-[#B0BEC5] file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:bg-[#243B55] file:text-white" />
                <p className="text-xs text-gray-500 mt-1">Uploaded to the association right after it's created. PNG/JPG/SVG/WEBP/GIF, under 2 MB.</p>
              </div>
              <div className="flex gap-4">
                <Check label="License RE" checked={form.licensed_re} onChange={(v) => setForm({ ...form, licensed_re: v })} />
                <Check label="License CRE" checked={form.licensed_cre} onChange={(v) => setForm({ ...form, licensed_cre: v })} />
              </div>
              <div className="bg-[#0A0E27] border border-[#546E7A]/40 rounded p-2.5 space-y-2">
                <div className="text-xs font-semibold text-[#00D4FF]">AiDA contract terms</div>
                <div>
                  <Input label="Flat monthly fee from the AiDA contract ($/mo)" type="number" min="0" value={form.monthly_support_fee} onChange={(v) => setForm({ ...form, monthly_support_fee: v })} />
                  <p className="text-xs text-gray-500 mt-1">Manual entry. The flat monthly amount this association pays AiDA per your signed agreement. The per-seat revenue share is calculated automatically from authorized seats. Leave blank until a contract is signed.</p>
                </div>
                <div>
                  <Input label="AiDA seat revenue share (%)" type="number" min="0" max="100" value={form.aida_revenue_share_pct} onChange={(v) => setForm({ ...form, aida_revenue_share_pct: v })} />
                  <p className="text-xs text-gray-500 mt-1">0–100. Super-admin only — never shown to associations. Leave blank until a contract is signed.</p>
                </div>
              </div>
              <Input label="Subscription term start (date)" type="date" value={form.term_start} onChange={(v) => setForm({ ...form, term_start: v })} />
              <div className="bg-[#0A0E27] border border-[#546E7A]/40 rounded p-2.5">
                <Check label="Free launch period — first 30 days free" checked={Number(form.launch_period_days) > 0} onChange={(v) => setForm({ ...form, launch_period_days: v ? 30 : 0 })} />
                <p className="text-xs text-gray-500 mt-1">When on, AiDA bills the association nothing for the first 30 days; billing starts after that.</p>
              </div>
              <button type="submit" disabled={creating}
                className="w-full py-2 rounded-md bg-[#00D4FF] text-[#0A0E27] font-semibold disabled:opacity-50">
                {creating ? 'Creating…' : 'Create Association'}
              </button>
            </form>
          </div>
          <div className="lg:col-span-2 space-y-3">
            {list.length === 0 && <p className="text-[#B0BEC5]">No associations yet.</p>}
            {list.map((a) => (
              <div key={a.id} className="bg-[#1A2744] rounded-xl border border-[#546E7A]/30 p-4">
                <div className="flex flex-wrap justify-between items-center gap-2">
                  <div className="min-w-0">
                    {a.logo_url && <img src={logoSrc(a.logo_url)} alt="" className="h-6 w-auto rounded mb-1" />}
                    <div className="text-white font-semibold break-words">
                      {a.display_name} <span className="text-gray-500 text-sm">/ {a.name}</span>
                    </div>
                    <div className="text-xs text-[#B0BEC5] break-words">
                      {[a.licensed_re && 'RE', a.licensed_cre && 'CRE'].filter(Boolean).join(' + ')} · {a.status} · AiDA billing {a.aida_billing_start || '—'}
                    </div>
                  </div>
                  <button onClick={() => setSelected(selected === a.id ? null : a.id)}
                    className="shrink-0 px-3 py-2 text-sm text-[#00D4FF] hover:bg-[#243B55] rounded-md">
                    {selected === a.id ? 'Close' : 'Manage admins'}
                  </button>
                </div>
                {selected === a.id && (
                  <>
                    <div className="mt-3 border-t border-[#546E7A]/20 pt-3">
                      <p className="text-xs text-[#B0BEC5] mb-2">Association logo (shown in-app and on the signup page):</p>
                      <LogoUploader associationId={a.id} currentUrl={a.logo_url} onUploaded={() => load()} />
                    </div>
                    <AiDATermsEditor a={a} onSaved={load} />
                    <AssociationAdmins associationId={a.id} />
                  </>
                )}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function AiDATermsEditor({ a, onSaved }) {
  const [fee, setFee] = useState(a.monthly_support_fee ?? '');
  const [share, setShare] = useState(a.aida_revenue_share_pct ?? '');
  const [msg, setMsg] = useState('');
  const save = async () => {
    setMsg('');
    const body = {
      monthly_support_fee: fee === '' ? null : Number(fee),
      aida_revenue_share_pct: share === '' ? null : Number(share),
    };
    const res = await authFetch(`${API}/api/associations/${a.id}`, { method: 'PATCH', body: JSON.stringify(body) });
    const d = await res.json().catch(() => ({}));
    if (!res.ok) { setMsg(d.detail || 'Save failed'); return; }
    setMsg('Saved.'); onSaved();
  };
  return (
    <div className="mt-3 border-t border-[#546E7A]/20 pt-3">
      <p className="text-xs font-semibold text-[#00D4FF] mb-2">AiDA contract terms (super-admin only)</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div>
          <label className="block text-xs text-[#B0BEC5] mb-1">Flat monthly fee ($/mo)</label>
          <input type="number" min="0" value={fee} onChange={(e) => setFee(e.target.value)}
            className="w-full px-2 py-1.5 rounded bg-[#0A0E27] border border-[#546E7A]/40 text-white text-sm" />
        </div>
        <div>
          <label className="block text-xs text-[#B0BEC5] mb-1">Seat revenue share (%)</label>
          <input type="number" min="0" max="100" value={share} onChange={(e) => setShare(e.target.value)}
            className="w-full px-2 py-1.5 rounded bg-[#0A0E27] border border-[#546E7A]/40 text-white text-sm" />
        </div>
      </div>
      <div className="flex items-center gap-3 mt-2">
        <button onClick={save} className="px-3 py-1.5 text-xs bg-[#00D4FF] text-[#0A0E27] font-semibold rounded">Save terms</button>
        {msg && <span className="text-xs text-[#00E676]">{msg}</span>}
      </div>
    </div>
  );
}

function AssociationAdmins({ associationId }) {
  const [admins, setAdmins] = useState([]);
  const [form, setForm] = useState({ full_name: '', email: '', phone: '' });
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState('');

  const load = () => authFetch(`${API}/api/associations/${associationId}/admins`)
    .then((r) => (r.ok ? r.json() : [])).then(setAdmins);
  useEffect(() => { load(); }, [associationId]);

  const add = async (e) => {
    e.preventDefault(); setError(''); setInvite(null);
    try {
      const res = await authFetch(`${API}/api/associations/${associationId}/admins`,
        { method: 'POST', body: JSON.stringify(form) });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to add admin');
      setInvite(`${window.location.origin}${data.invite_path}`);
      setForm({ full_name: '', email: '', phone: '' });
      load();
    } catch (e) { setError(e.message); }
  };

  return (
    <div className="mt-3 border-t border-[#546E7A]/20 pt-3">
      <div className="space-y-1 mb-3">
        {admins.map((a) => (
          <div key={a.id} className="flex flex-wrap justify-between gap-2 text-sm">
            <span className="text-white min-w-0 break-words">{a.full_name} <span className="text-gray-500">({a.email})</span></span>
            <span className="text-[#B0BEC5] shrink-0">{a.has_password ? 'Active' : 'Invite pending'}</span>
          </div>
        ))}
      </div>
      <form onSubmit={add} className="flex flex-wrap gap-2 items-end">
        <input placeholder="Full name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required
          className="px-2 py-1.5 rounded bg-[#0A0E27] border border-[#546E7A]/40 text-white text-sm" />
        <input placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required
          className="px-2 py-1.5 rounded bg-[#0A0E27] border border-[#546E7A]/40 text-white text-sm" />
        <input placeholder="Phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
          className="px-2 py-1.5 rounded bg-[#0A0E27] border border-[#546E7A]/40 text-white text-sm" />
        <button type="submit" className="px-3 py-1.5 text-sm bg-[#00E676] text-[#0A0E27] font-semibold rounded">Add Admin</button>
      </form>
      {error && <p className="text-red-400 text-sm mt-2">{error}</p>}
      {invite && (
        <div className="mt-3 bg-[#0A0E27] border border-[#00D4FF]/40 rounded-md p-3">
          <p className="text-[#B0BEC5] text-xs mb-1">Invite link (email is stubbed — copy &amp; send this to the admin):</p>
          <div className="flex gap-2 items-center">
            <input readOnly value={invite}
              className="flex-1 min-w-0 px-2 py-1.5 rounded bg-[#1A2744] border border-[#546E7A]/40 text-[#00D4FF] text-xs" />
            <button onClick={() => navigator.clipboard && navigator.clipboard.writeText(invite)}
              className="px-3 py-1.5 text-sm bg-[#00D4FF] text-[#0A0E27] font-semibold rounded">Copy</button>
          </div>
        </div>
      )}
    </div>
  );
}
